"use client";

import React, { useEffect, useState } from "react";

import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Info } from "lucide-react";
import { SecuredFooter } from "./ui/secured-footer";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { createEightyEightPayTrade } from "../services/digitalWalletService";
import { useToast } from "../hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import PaymentLoader from "./PaymentLoader";

const ConfirmationDetails = ({
  onContinue,
  onFail,
  confirmData,
  paymentPayData,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [checked, setChecked] = useState(false);
  const [disableField, setDisableField] = useState(false);
  const [senderRef, setSenderRef] = useState("");
  const [receiverRef, setReceiverRef] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (checked === true) {
      setReceiverRef("");
    }
  }, [checked]);

  const handleChange = () => {
    if (checked === false) {
      setChecked(true);
      setDisableField(true);
    }
    if (checked === true) {
      setChecked(false);
      setDisableField(false);
    }
  };

  const payTradeData = {
    senderId: paymentPayData?.senderId,
    receiverEmail: paymentPayData?.receiverEmail,
    receiverPayId: paymentPayData?.receiverPayId,
    receiverPhone: paymentPayData?.receiverPhone,
    anonymous: checked || false,
    senderReference: senderRef || "",
    receiverReference: receiverRef || "",
    // serviceFee: paymentPayData?.serviceFee,
    serviceFee: 0,
    amount: paymentPayData?.amount,
    totalAmount: paymentPayData?.totalAmount,
    assetId: paymentPayData?.assetId,
  };

  const handleConfirm = async () => {
    setIsLoading(true);

    try {
      const payTrade = await createEightyEightPayTrade(payTradeData);

      // Validate response structure before accessing nested properties
      if (!payTrade?.data?.data) {
        throw new Error("Invalid response structure from server");
      }

      const newTransaction = payTrade.data.data.newTransaction;
      const senderBalance = payTrade.data.data.senderBalance;

      if (!newTransaction) {
        throw new Error(
          "Transaction creation failed - no transaction returned"
        );
      }

      if (onContinue) {
        onContinue(newTransaction, senderBalance);
      }
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    } catch (error) {
      // Extract error message
      const errorMessage =
        error?.message ||
        error?.toString() ||
        "Failed to submit payment. Please try again.";

      // Log error for debugging
      console.error("Payment submission error:", error);

      // Call onFail callback to show failed screen
      if (onFail) {
        setIsLoading(false);
        onFail(errorMessage);
        return;
      }

      // Fallback: Show error toast notification to user
      toast({
        description: errorMessage,
        variant: "eighty",
      });
      setIsLoading(false);
    }
  };

  // Show loader while processing payment
  if (isLoading) {
    return <PaymentLoader />;
  }

  return (
    <Card className="w-full bg-white rounded-2xl border border-[#EBE9EF] !shadow-none relative">
      <CardContent className="px-8 py-8 w-full">
        <h2 className="text-[20px] font-semibold text-[#33125F] mb-8">
          Confirm the following details
        </h2>

        {/* Your reference */}
        <div className="mb-6">
          <label className="block text-[14px] text-[#6D6584] mb-3">
            Your reference
          </label>
          <div className="border border-[#CECBD7] rounded-2xl h-[56px] px-4 flex items-center">
            <input
              type="text"
              placeholder=""
              value={senderRef}
              onChange={(e) =>
                setSenderRef(e.target.value.replace(/[^a-zA-Z\s]/g, ""))
              }
              className="flex-1 outline-none text-[16px] bg-transparent text-[#423E51]"
            />
          </div>
        </div>

        {/* Their reference */}
        {disableField === false && (
          <div className="mb-4">
            <label className="block text-[14px] text-[#6D6584] mb-3">
              Their reference
            </label>
            <div className="border border-[#CECBD7] rounded-2xl h-[56px] px-4 flex items-center">
              <input
                type="text"
                placeholder=""
                value={receiverRef}
                onChange={(e) =>
                  setReceiverRef(e.target.value.replace(/[^a-zA-Z\s]/g, ""))
                }
                className="flex-1 outline-none text-[16px] bg-transparent text-[#423E51]"
              />
            </div>
          </div>
        )}

        {/* Send anonymously */}
        <div className="flex items-center gap-3 mb-6">
          <input
            type="checkbox"
            id="customCheckbox"
            checked={checked}
            onChange={handleChange}
            className="w-8 h-8 cursor-pointer border-2 border-[#CECBD7] rounded"
          />
          <label
            htmlFor="customCheckbox"
            className="text-[16px] font-semibold text-[#6D6584]"
          >
            Send anonymously
          </label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info size={16} className="text-[#EB3366] cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Your identity will be hidden from the recipient</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Transaction details */}
        <p className="text-[14px] font-semibold text-[#6D6584] mb-3">
          Transaction details
        </p>
        <div className="border border-[#EBE9EF] rounded-2xl divide-y divide-[#EBE9EF]">
          <div className="px-4 py-3 flex justify-between items-center">
            <span className="text-[14px] text-[#423E51]">You send</span>
            <span className="text-right">
              <span className="text-[16px] font-semibold text-[#423E51] block">
                {confirmData?.amount}
              </span>
              {confirmData?.amountSecondary && (
                <span className="text-[14px] text-[#6D6584] block mt-0.5">
                  ≈ {confirmData.amountSecondary}
                </span>
              )}
            </span>
          </div>

          <div className="px-4 py-3 flex justify-between items-center">
            <span className="flex items-center gap-2 text-[14px] text-[#423E51]">
              Service charge
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={16} className="text-[#EB3366] cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>88Pay transactions have no service fees</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
            <span className="text-[16px] font-semibold text-[#423E51]">
              R {confirmData?.serviceFee || "0.00"}
            </span>
          </div>

          <div className="px-4 py-3 flex justify-between items-center">
            <span className="text-[14px] font-semibold text-[#423E51]">
              Total cost
            </span>
            <span className="text-[16px] font-semibold text-[#423E51]">
              {confirmData?.totalAmount}
            </span>
          </div>
        </div>
      </CardContent>

      {/* Confirm Button */}
      <div className="flex justify-center w-full px-6 pb-8">
        <Button
          onClick={handleConfirm}
          className="h-[48px] w-[302px] text-[14px] font-semibold text-white rounded-full bg-[linear-gradient(70.19deg,#57249C_10.66%,#EB3366_113.47%)] hover:opacity-90"
        >
          Confirm Payment
        </Button>
      </div>

      <SecuredFooter />
    </Card>
  );
};

export default ConfirmationDetails;
